(function () {
  "use strict";

  var MAX_BYTES = 20 * 1024 * 1024;
  var ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
  var EVENT_TYPES = [
    ["exam", "Exam"], ["final_exam", "Final exam"], ["quiz", "Quiz"],
    ["homework", "Homework"], ["assignment", "Assignment"], ["paper", "Paper"],
    ["project", "Project"], ["presentation", "Presentation"], ["lab", "Lab"],
    ["discussion_post", "Discussion post"], ["reading_deadline", "Reading deadline"],
    ["class_cancellation", "Class cancellation"], ["major_deadline", "Major deadline"],
    ["other", "Other"]
  ];
  var PROCESS_MESSAGES = [
    ["Opening your syllabus", "I’m getting the pages in order and finding the course details."],
    ["Reading the fine print", "I’m scanning for exams, assignments, projects, labs, and important deadlines."],
    ["Connecting the dates", "I’m matching each deadline with its source page so you can verify it."],
    ["Checking for tricky wording", "I’m flagging anything ambiguous instead of guessing a missing date or time."],
    ["Building your review list", "Almost there — I’m organizing the proposed events before you see them."],
    ["Giving everything one last look", "Your calendar is still untouched. You will choose exactly what gets added."]
  ];
  var state = { bridge: null, user: null, upload: null, uploads: [], proposals: [], duplicates: {}, polling: null, progressTimer: null, progressStep: 0 };
  var el = {};

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  window.addEventListener("memoryfuel:study-lounge-ready", connectBridge);
  window.addEventListener("memoryfuel:auth-state", function (event) {
    state.user = event.detail && event.detail.user ? event.detail.user : null;
    if (state.user) loadRecentUpload();
    else resetForLogout();
  });

  function init() {
    var root = document.getElementById("mf-smart-syllabus");
    if (!root || root.getAttribute("data-smart-ready") === "true") return;
    root.setAttribute("data-smart-ready", "true");
    el.root = root;
    el.drawer = document.getElementById("mf-smart-planner-drawer");
    el.open = document.getElementById("mf-smart-planner-open");
    el.close = document.getElementById("mf-smart-planner-close");
    el.course = document.getElementById("mf-syllabus-course");
    el.file = document.getElementById("mf-syllabus-file");
    el.revised = document.getElementById("mf-syllabus-revised");
    el.upload = document.getElementById("mf-syllabus-upload");
    el.retry = document.getElementById("mf-syllabus-retry");
    el.undo = document.getElementById("mf-syllabus-undo");
    el.statusTitle = document.getElementById("mf-syllabus-status-title");
    el.status = document.getElementById("mf-syllabus-status");
    el.success = document.getElementById("mf-smart-success");
    el.successMessage = document.getElementById("mf-smart-success-message");
    el.newSyllabus = document.getElementById("mf-syllabus-new");
    el.library = document.getElementById("mf-syllabus-library-list");
    el.review = document.getElementById("mf-syllabus-review");
    el.proposals = document.getElementById("mf-syllabus-proposals");
    el.confirm = document.getElementById("mf-syllabus-confirm");

    el.open.addEventListener("click", openPlanner);
    el.close.addEventListener("click", closePlanner);
    el.drawer.querySelectorAll("[data-smart-close]").forEach(function (button) { button.addEventListener("click", closePlanner); });
    el.newSyllabus.addEventListener("click", startNewSyllabus);
    el.upload.addEventListener("click", uploadSyllabus);
    el.retry.addEventListener("click", retryExtraction);
    el.undo.addEventListener("click", undoLastImport);
    el.confirm.addEventListener("click", confirmSelected);
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && !el.drawer.hidden) closePlanner();
    });
    connectBridge();
  }

  function connectBridge() {
    if (!el.root || !window.MemoryFuelStudyLounge) return;
    state.bridge = window.MemoryFuelStudyLounge;
    state.user = state.bridge.getCurrentUser();
    if (state.user) loadRecentUpload();
  }

  function openPlanner() {
    el.drawer.hidden = false;
    document.querySelector(".mf-study-lounge")?.classList.add("mf-smart-planner-is-open");
    window.requestAnimationFrame(function () { el.close.focus(); });
  }

  function closePlanner() {
    el.drawer.hidden = true;
    document.querySelector(".mf-study-lounge")?.classList.remove("mf-smart-planner-is-open");
    el.open.focus();
  }

  function startNewSyllabus() {
    stopProgressMessages();
    state.upload = null;
    state.proposals = [];
    state.duplicates = {};
    el.file.value = "";
    el.course.value = "";
    el.revised.checked = false;
    el.root.classList.remove("is-success");
    el.success.hidden = true;
    renderProposals();
    renderStatus();
    el.course.focus();
  }

  async function uploadSyllabus() {
    if (!requireLogin()) return;
    var file = el.file.files[0];
    var course = el.course.value.trim();
    if (!file) return setMessage("Choose a syllabus", "Select a PDF or image first.");
    if (ALLOWED_TYPES.indexOf(file.type) === -1 || !/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) {
      return setMessage("Unsupported file", "Use PDF, JPG, JPEG, PNG, or WEBP.");
    }
    if (file.size < 1 || file.size > MAX_BYTES) return setMessage("File is too large", "The maximum file size is 20 MB.");

    setBusy(true);
    setMessage("Uploading privately", "The file is being placed in your private syllabus folder.");
    var db = state.bridge.db;
    var uploadId = crypto.randomUUID();
    var extension = (file.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "");
    var path = state.user.id + "/" + uploadId + "/source." + extension;
    var revisionOf = el.revised.checked && state.upload ? state.upload.id : null;
    var created = await db.from("syllabus_uploads").insert({
      id: uploadId,
      user_id: state.user.id,
      original_name: file.name.slice(0, 255),
      storage_path: path,
      mime_type: file.type,
      file_size_bytes: file.size,
      course_hint: course,
      revision_of: revisionOf,
      status: "created"
    }).select("*").single();
    if (created.error) {
      setBusy(false);
      return setMessage("Upload could not start", friendlyError(created.error));
    }
    state.upload = created.data;
    state.uploads = [created.data].concat(state.uploads.filter(function (item) { return item.id !== created.data.id; }));
    renderUploads();

    var stored = await db.storage.from("syllabi").upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
    if (stored.error) {
      await db.from("syllabus_uploads").update({ status: "validation_failed", last_error_code: "upload_failed", last_error_message: stored.error.message.slice(0, 500) }).eq("id", uploadId);
      setBusy(false);
      return setMessage("Private upload failed", friendlyError(stored.error));
    }
    await db.from("syllabus_uploads").update({ status: "queued" }).eq("id", uploadId);
    state.upload.status = "queued";
    state.uploads = state.uploads.map(function (item) { return item.id === uploadId ? Object.assign({}, item, { status: "queued" }) : item; });
    renderUploads();
    startProgressMessages();
    beginPolling(uploadId);
    var processed = await db.functions.invoke("process-syllabus", { body: { upload_id: uploadId } });
    if (processed.error) {
      await loadUpload(uploadId);
      if (state.upload && state.upload.status === "processing") setMessage("Still processing", "Extraction is continuing. This panel will update automatically.");
    } else {
      await loadUpload(uploadId);
    }
    setBusy(false);
  }

  async function retryExtraction() {
    if (!requireLogin() || !state.upload) return;
    var expiredLease = state.upload.status === "processing" && state.upload.processing_lease_until
      && new Date(state.upload.processing_lease_until).getTime() < Date.now();
    if (state.upload.status !== "processing_failed" && !expiredLease) return setMessage("Retry unavailable", "Only a failed or interrupted extraction can be retried.");
    setBusy(true);
    setMessage("Retrying extraction", "Your existing private upload is being processed again.");
    beginPolling(state.upload.id);
    await state.bridge.db.functions.invoke("process-syllabus", { body: { upload_id: state.upload.id } });
    await loadUpload(state.upload.id);
    setBusy(false);
  }

  async function loadRecentUpload() {
    if (!state.bridge || !state.user) return;
    await loadUploads();
    if (!state.uploads.length) {
      state.upload = null;
      state.proposals = [];
      renderStatus();
      renderProposals();
      return;
    }
    state.upload = state.uploads[0];
    if (["queued", "processing"].indexOf(state.upload.status) >= 0) beginPolling(state.upload.id);
    await loadProposals();
    renderStatus();
  }

  async function loadUploads() {
    if (!state.bridge || !state.user) return;
    var result = await state.bridge.db.from("syllabus_uploads")
      .select("*")
      .eq("user_id", state.user.id).neq("status", "deleted").order("created_at", { ascending: false }).limit(20);
    state.uploads = result.data || [];
    renderUploads();
  }

  async function loadUpload(uploadId) {
    var result = await state.bridge.db.from("syllabus_uploads").select("*").eq("id", uploadId).eq("user_id", state.user.id).maybeSingle();
    if (!result.error && result.data) {
      state.upload = result.data;
      state.uploads = state.uploads.map(function (item) { return item.id === result.data.id ? Object.assign({}, item, result.data) : item; });
      renderUploads();
    }
    if (state.upload && ["ready_for_review", "imported"].indexOf(state.upload.status) >= 0) await loadProposals();
    renderStatus();
  }

  function renderUploads() {
    if (!el.library) return;
    el.library.innerHTML = "";
    if (!state.user) {
      var loginEmpty = node("p", "mf-syllabus-library-empty");
      loginEmpty.textContent = "Log in to view your private syllabus uploads.";
      el.library.appendChild(loginEmpty);
      return;
    }
    if (!state.uploads.length) {
      var empty = node("p", "mf-syllabus-library-empty");
      empty.textContent = "No syllabus files uploaded yet.";
      el.library.appendChild(empty);
      return;
    }
    state.uploads.forEach(function (upload) {
      var row = node("article", "mf-syllabus-file-row");
      var icon = node("span", "mf-syllabus-file-icon");
      var details = node("div", "mf-syllabus-file-details");
      var name = document.createElement("strong");
      var meta = document.createElement("span");
      var actions = node("div", "mf-syllabus-file-actions");
      var view = document.createElement("button");
      var flashcards = document.createElement("button");
      var remove = document.createElement("button");
      icon.textContent = upload.mime_type === "application/pdf" ? "PDF" : "IMG";
      name.textContent = upload.original_name;
      meta.textContent = [upload.course_hint || "Course not named", uploadStatusLabel(upload.status), new Date(upload.created_at).toLocaleDateString()].join(" · ");
      view.type = "button";
      view.textContent = "View";
      view.addEventListener("click", function () { viewUploadedSyllabus(upload, view); });
      flashcards.type = "button";
      flashcards.textContent = "Create flashcards";
      flashcards.disabled = ["queued", "processing", "failed", "deleted"].indexOf(upload.status) >= 0;
      flashcards.title = flashcards.disabled ? "Wait until this syllabus is ready." : "Open this private syllabus in Flashcard Studio";
      flashcards.addEventListener("click", function () {
        var close = document.getElementById("mf-smart-planner-close");
        if (close) close.click();
        window.dispatchEvent(new CustomEvent("memoryfuel:flashcards-open", {
          detail: { tab: "create", syllabus_upload_id: upload.id }
        }));
      });
      remove.type = "button";
      remove.className = "mf-syllabus-delete";
      remove.textContent = "Delete";
      remove.disabled = ["queued", "processing"].indexOf(upload.status) >= 0;
      if (remove.disabled) remove.title = "Wait for processing to finish before deleting this syllabus.";
      remove.addEventListener("click", function () { deleteUploadedSyllabus(upload, remove); });
      details.appendChild(name);
      details.appendChild(meta);
      actions.appendChild(view);
      actions.appendChild(flashcards);
      actions.appendChild(remove);
      row.appendChild(icon);
      row.appendChild(details);
      row.appendChild(actions);
      el.library.appendChild(row);
    });
  }

  async function viewUploadedSyllabus(upload, button) {
    if (!requireLogin()) return;
    button.disabled = true;
    var result = await state.bridge.db.storage.from("syllabi").createSignedUrl(upload.storage_path, 120);
    button.disabled = false;
    if (result.error || !result.data || !result.data.signedUrl) return setMessage("File could not open", "Please try viewing this private syllabus again.");
    window.open(result.data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function deleteUploadedSyllabus(upload, button) {
    if (!requireLogin()) return;
    if (["queued", "processing"].indexOf(upload.status) >= 0) return setMessage("Still reading this syllabus", "Wait for processing to finish before deleting it.");
    if (!window.confirm('Delete "' + upload.original_name + '"? This permanently deletes the syllabus file, all extracted content, and every calendar event imported from this syllabus. This cannot be undone.')) return;
    button.disabled = true;
    setMessage("Deleting syllabus and calendar content", "Removing the private file, extracted dates, and every calendar event owned by this syllabus.");
    var storageResult = await state.bridge.db.storage.from("syllabi").remove([upload.storage_path]);
    if (storageResult.error) {
      button.disabled = false;
      return setMessage("Syllabus was not deleted", "The private file could not be removed. Nothing else was changed.");
    }
    var deleteResult = await state.bridge.db.rpc("delete_syllabus_content", { p_upload_id: upload.id });
    if (deleteResult.error) {
      button.disabled = false;
      return setMessage("Calendar cleanup needs attention", "The source file was removed, but the syllabus content could not be cleared from your calendar. Please try Delete again.");
    }
    state.uploads = state.uploads.filter(function (item) { return item.id !== upload.id; });
    if (state.upload && state.upload.id === upload.id) {
      state.upload = state.uploads[0] || null;
      state.proposals = [];
      state.duplicates = {};
      if (state.upload && ["ready_for_review", "imported"].indexOf(state.upload.status) >= 0) await loadProposals();
      else renderProposals();
    }
    renderUploads();
    renderStatus();
    await state.bridge.refreshCalendar();
    var removed = deleteResult.data && Number(deleteResult.data.calendar_events_removed) ? Number(deleteResult.data.calendar_events_removed) : 0;
    setMessage("Syllabus and calendar content deleted", "The private file, extracted content, and " + removed + " calendar " + plural(removed, "event", "events") + " owned by this syllabus were removed.");
  }

  function uploadStatusLabel(status) {
    var labels = { created: "Preparing", uploaded: "Uploaded", queued: "Queued", processing: "Reading", ready_for_review: "Ready to review", importing: "Importing", imported: "Added to calendar", validation_failed: "Needs attention", processing_failed: "Processing failed", import_failed: "Import failed", expired: "Expired" };
    return labels[status] || "Uploaded";
  }

  async function loadProposals() {
    if (!state.upload) return;
    var result = await state.bridge.db.from("syllabus_event_proposals").select("*")
      .eq("upload_id", state.upload.id).eq("user_id", state.user.id).order("source_page", { ascending: true }).order("created_at", { ascending: true });
    state.proposals = result.data || [];
    state.duplicates = {};
    var duplicates = await state.bridge.db.rpc("find_syllabus_duplicates", { p_upload_id: state.upload.id });
    (duplicates.data || []).forEach(function (duplicate) { state.duplicates[duplicate.proposal_id] = duplicate; });
    renderProposals();
  }

  function beginPolling(uploadId) {
    if (state.polling) window.clearInterval(state.polling);
    var attempts = 0;
    state.polling = window.setInterval(async function () {
      attempts += 1;
      await loadUpload(uploadId);
      if (!state.upload || ["queued", "processing"].indexOf(state.upload.status) === -1 || attempts >= 80) {
        window.clearInterval(state.polling);
        state.polling = null;
      }
    }, 3000);
  }

  function renderStatus() {
    if (!state.upload) {
      stopProgressMessages();
      setMessage("Nothing uploaded yet", "Your file stays private. Dates are never added without your confirmation.");
      el.retry.hidden = true;
      el.root.classList.remove("is-success");
      el.success.hidden = true;
      el.open.innerHTML = '<span aria-hidden="true">✦</span> Import syllabus with AI';
      return;
    }
    var labels = {
      created: ["Preparing upload", "Creating your private upload record."],
      uploaded: ["Upload ready", "Ready to begin extraction."],
      ready_for_review: ["Review required", state.proposals.length + " proposed " + plural(state.proposals.length, "event", "events") + " waiting for you."],
      importing: ["Importing selected events", "Only the events you selected are being added."],
      imported: ["Import complete", "Approved events were added to your Study Calendar."],
      validation_failed: ["Upload needs attention", state.upload.last_error_message || "The file did not pass validation."],
      processing_failed: ["Extraction failed", state.upload.last_error_message || "You can safely retry this upload."],
      import_failed: ["Import failed", state.upload.last_error_message || "No partial calendar import was saved."],
      expired: ["Upload expired", "Upload the current syllabus again if you still need it."],
      deleted: ["Upload deleted", "The private source file has been removed."]
    };
    var isProcessing = ["queued", "processing"].indexOf(state.upload.status) >= 0;
    if (isProcessing) startProgressMessages();
    else stopProgressMessages();
    var message = isProcessing ? PROCESS_MESSAGES[state.progressStep % PROCESS_MESSAGES.length] : (labels[state.upload.status] || ["Smart Syllabus", "Check the latest upload status."]);
    setMessage(message[0], message[1]);
    var isSuccess = state.upload.status === "imported";
    el.root.classList.toggle("is-success", isSuccess);
    el.success.hidden = !isSuccess;
    if (isSuccess) {
      var importedCount = state.proposals.filter(function (proposal) { return proposal.review_status === "imported"; }).length;
      el.successMessage.textContent = importedCount + " selected " + plural(importedCount, "event is", "events are") + " now in your Study Calendar.";
      el.open.innerHTML = '<span aria-hidden="true">✓</span> Last syllabus added';
    } else {
      el.open.innerHTML = '<span aria-hidden="true">✦</span> Import syllabus with AI';
    }
    var expiredLease = state.upload.status === "processing" && state.upload.processing_lease_until
      && new Date(state.upload.processing_lease_until).getTime() < Date.now();
    el.retry.hidden = state.upload.status !== "processing_failed" && !expiredLease;
    el.review.hidden = state.upload.status !== "ready_for_review" || !state.proposals.length;
  }

  function startProgressMessages() {
    if (state.progressTimer) return;
    state.progressStep = 0;
    setMessage(PROCESS_MESSAGES[0][0], PROCESS_MESSAGES[0][1]);
    state.progressTimer = window.setInterval(function () {
      if (!state.upload || ["queued", "processing"].indexOf(state.upload.status) === -1) return stopProgressMessages();
      state.progressStep = (state.progressStep + 1) % PROCESS_MESSAGES.length;
      setMessage(PROCESS_MESSAGES[state.progressStep][0], PROCESS_MESSAGES[state.progressStep][1]);
    }, 4200);
  }

  function stopProgressMessages() {
    if (state.progressTimer) window.clearInterval(state.progressTimer);
    state.progressTimer = null;
  }

  function renderProposals() {
    el.proposals.innerHTML = "";
    if (!state.proposals.length) {
      el.review.hidden = true;
      return;
    }
    state.proposals.forEach(function (proposal) {
      var duplicate = state.duplicates[proposal.id];
      var warnings = Array.isArray(proposal.warnings) ? proposal.warnings.slice() : [];
      if (proposal.year_inferred) warnings.push("Year inferred");
      if (proposal.date_inferred) warnings.push("Date inferred");
      var flagged = !proposal.event_date || !proposal.course || Number(proposal.confidence) < 0.7 || warnings.length;
      var card = node("article", "mf-proposal" + (flagged ? " is-flagged" : "") + (duplicate ? " is-duplicate" : ""));
      card.setAttribute("data-proposal-id", proposal.id);
      var selected = document.createElement("input");
      selected.type = "checkbox";
      selected.className = "mf-proposal-select";
      selected.checked = proposal.review_status !== "rejected" && proposal.review_status !== "skipped_duplicate";
      selected.disabled = proposal.review_status === "imported";
      selected.setAttribute("aria-label", "Select " + proposal.title + " for calendar import");
      card.appendChild(selected);

      var fields = node("div", "mf-proposal-fields");
      var title = field("Title", "text", proposal.title, "title");
      var course = field("Course", "text", proposal.course, "course");
      var type = selectField("Type", EVENT_TYPES, proposal.event_type, "event_type");
      var date = field("Date", "date", proposal.event_date || "", "event_date");
      var time = field("Time", "time", proposal.event_time ? String(proposal.event_time).slice(0, 5) : "", "event_time");
      var allDay = node("label", "mf-proposal-all-day");
      var allDayText = document.createElement("span");
      var allDayInput = document.createElement("input");
      allDayText.textContent = "All day";
      allDayInput.type = "checkbox";
      allDayInput.checked = proposal.all_day;
      allDayInput.setAttribute("data-field", "all_day");
      allDay.appendChild(allDayText);
      allDay.appendChild(allDayInput);
      fields.appendChild(title); fields.appendChild(course); fields.appendChild(type); fields.appendChild(date); fields.appendChild(time); fields.appendChild(allDay);

      var recurrence = field("Recurrence when stated", "text", proposal.recurrence_rule || "", "recurrence_rule");
      recurrence.style.gridColumn = "1 / -1";
      fields.appendChild(recurrence);

      var details = node("div", "mf-proposal-details");
      var source = node("span", "mf-proposal-source");
      source.textContent = "Page " + proposal.source_page + ": “" + proposal.exact_source_text + "”";
      var confidence = node("span", "mf-proposal-confidence");
      confidence.textContent = Math.round(Number(proposal.confidence) * 100) + "% confidence";
      details.appendChild(source); details.appendChild(confidence);
      var warningWrap = node("div", "mf-proposal-warnings");
      unique(warnings).forEach(function (warning) {
        var badge = node("span", "mf-proposal-warning");
        badge.textContent = warning;
        warningWrap.appendChild(badge);
      });
      details.appendChild(warningWrap);
      fields.appendChild(details);

      if (duplicate) {
        var duplicateChoice = node("label", "mf-duplicate-choice");
        var duplicateLabel = document.createElement("span");
        duplicateLabel.textContent = "Possible duplicate: " + duplicate.existing_title + " on " + duplicate.existing_date;
        var resolution = document.createElement("select");
        resolution.setAttribute("data-field", "duplicate_resolution");
        [["", "Choose action"], ["skip", "Skip this duplicate"], ["import_anyway", "Import anyway"]].forEach(function (optionData) {
          var option = document.createElement("option"); option.value = optionData[0]; option.textContent = optionData[1]; resolution.appendChild(option);
        });
        resolution.value = proposal.duplicate_resolution || "";
        duplicateChoice.appendChild(duplicateLabel); duplicateChoice.appendChild(resolution); fields.appendChild(duplicateChoice);
      }

      fields.querySelectorAll("[data-field]").forEach(function (input) {
        input.disabled = proposal.review_status === "imported";
        input.addEventListener("change", function () {
          if (input.getAttribute("data-field") === "all_day" && input.checked) {
            fields.querySelector('[data-field="event_time"]').value = "";
          }
          if (input.getAttribute("data-field") === "event_time" && input.value) {
            fields.querySelector('[data-field="all_day"]').checked = false;
          }
          saveProposal(card, proposal.id);
        });
      });
      card.appendChild(fields);
      el.proposals.appendChild(card);
    });
    el.review.hidden = false;
  }

  async function saveProposal(card, proposalId) {
    var values = proposalValues(card);
    var update = {
      title: values.title,
      course: values.course,
      event_type: values.event_type,
      event_date: values.event_date || null,
      event_time: values.all_day ? null : (values.event_time || null),
      all_day: values.all_day,
      recurrence_rule: values.recurrence_rule || null,
      duplicate_resolution: values.duplicate_resolution || null,
      review_status: "approved",
      user_edited: true
    };
    var result = await state.bridge.db.from("syllabus_event_proposals").update(update)
      .eq("id", proposalId).eq("user_id", state.user.id).select("*").single();
    if (result.error) setMessage("Could not save edit", friendlyError(result.error));
    else state.proposals = state.proposals.map(function (item) { return item.id === proposalId ? result.data : item; });
  }

  async function confirmSelected() {
    if (!requireLogin() || !state.upload) return;
    var cards = Array.prototype.slice.call(el.proposals.querySelectorAll(".mf-proposal"));
    var selectedCards = cards.filter(function (card) { return card.querySelector(".mf-proposal-select").checked && !card.querySelector(".mf-proposal-select").disabled; });
    if (!selectedCards.length) return setMessage("Select at least one event", "Choose the dates you want added to your calendar.");
    var invalid = selectedCards.find(function (card) {
      var value = proposalValues(card);
      return !value.title || !value.course || !value.event_date || (!value.all_day && !value.event_time) || (state.duplicates[card.getAttribute("data-proposal-id")] && !value.duplicate_resolution);
    });
    if (invalid) {
      invalid.scrollIntoView({ behavior: "smooth", block: "center" });
      return setMessage("Review flagged fields", "Every selected event needs a title, course, date, valid time/all-day choice, and duplicate decision.");
    }
    setBusy(true);
    setMessage("Saving your review", "Only your selected events will be imported.");
    await Promise.all(selectedCards.map(function (card) { return saveProposal(card, card.getAttribute("data-proposal-id")); }));
    var proposalIds = selectedCards.map(function (card) { return card.getAttribute("data-proposal-id"); });
    var result = await state.bridge.db.rpc("confirm_syllabus_import", {
      p_upload_id: state.upload.id,
      p_proposal_ids: proposalIds,
      p_idempotency_key: crypto.randomUUID()
    });
    setBusy(false);
    if (result.error) return setMessage("Calendar import stopped safely", friendlyError(result.error));
    setMessage("Events added", result.data.imported + " selected " + plural(result.data.imported, "event was", "events were") + " added. " + result.data.skipped + " duplicate " + plural(result.data.skipped, "was", "were") + " skipped.");
    await state.bridge.refreshCalendar();
    await loadUpload(state.upload.id);
  }

  async function undoLastImport() {
    if (!requireLogin()) return;
    if (!window.confirm("Undo your most recent Smart Syllabus import? Only events from that import will be removed.")) return;
    setBusy(true);
    var result = await state.bridge.db.rpc("undo_last_syllabus_import");
    setBusy(false);
    if (result.error) return setMessage("Nothing was changed", friendlyError(result.error));
    setMessage("Import undone", result.data.events_removed + " imported " + plural(result.data.events_removed, "event was", "events were") + " removed from your calendar.");
    await state.bridge.refreshCalendar();
    await loadRecentUpload();
  }

  function proposalValues(card) {
    function value(name) { var input = card.querySelector('[data-field="' + name + '"]'); return input ? input.value.trim() : ""; }
    var allDay = card.querySelector('[data-field="all_day"]').checked;
    return {
      title: value("title"), course: value("course"), event_type: value("event_type"),
      event_date: value("event_date"), event_time: value("event_time"), all_day: allDay,
      recurrence_rule: value("recurrence_rule"), duplicate_resolution: value("duplicate_resolution")
    };
  }

  function field(labelText, type, value, name) {
    var label = document.createElement("label");
    var span = document.createElement("span");
    var input = document.createElement("input");
    span.textContent = labelText; input.type = type; input.value = value; input.setAttribute("data-field", name);
    if (name === "title") input.maxLength = 160;
    if (name === "course") input.maxLength = 120;
    if (name === "recurrence_rule") input.maxLength = 500;
    label.appendChild(span); label.appendChild(input); return label;
  }

  function selectField(labelText, options, value, name) {
    var label = document.createElement("label"); var span = document.createElement("span"); var select = document.createElement("select");
    span.textContent = labelText; select.setAttribute("data-field", name);
    options.forEach(function (optionData) { var option = document.createElement("option"); option.value = optionData[0]; option.textContent = optionData[1]; select.appendChild(option); });
    select.value = value; label.appendChild(span); label.appendChild(select); return label;
  }

  function node(tag, className) { var result = document.createElement(tag); result.className = className; return result; }
  function unique(values) { return values.filter(function (value, index, list) { return value && list.indexOf(value) === index; }); }
  function plural(count, one, many) { return count === 1 ? one : many; }
  function friendlyError(error) { return (error && (error.message || error.error_description)) ? String(error.message || error.error_description).slice(0, 300) : "Please try again."; }
  function requireLogin() {
    if (state.bridge && state.user) return true;
    setMessage("Login required", "Log in to use private Smart Syllabus uploads.");
    return false;
  }
  function setMessage(title, message) {
    el.statusTitle.textContent = title; el.status.textContent = message;
  }
  function setBusy(busy) {
    el.upload.disabled = busy; el.retry.disabled = busy; el.confirm.disabled = busy; el.undo.disabled = busy;
  }
  function resetForLogout() {
    if (state.polling) window.clearInterval(state.polling);
    stopProgressMessages();
    state.polling = null; state.upload = null; state.uploads = []; state.proposals = []; state.duplicates = {};
    renderUploads(); renderProposals(); renderStatus();
  }
})();
